import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user } = useAuth();
  const isAdminView = user?.role === 'administrator' && (user?.active_view || 'admin') === 'admin';
  const [notifications, setNotifications] = useState(true);
  const [sharebirthday, setShareBirthday] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  
  const [adminSettings, setAdminSettings] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [overhead, setOverhead] = useState(14);
  const [cardFee, setCardFee] = useState(3);
  const [ptoAccrual, setPtoAccrual] = useState(0.0192);
  const [ptoCap, setPtoCap] = useState(40);
  const [showStudioRemainder, setShowStudioRemainder] = useState(false);
  const [birthdayMode, setBirthdayMode] = useState("draft");

  useEffect(() => {
    if (isAdminView) {
      loadAdminSettings();
    }
  }, [isAdminView]);

  const loadAdminSettings = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setAdminSettings(data);
      setOverhead(data.overhead_percent || 14);
      setCardFee(data.card_fee_percent || 3);
      setPtoAccrual(data.pto_accrual_rate || 0.0192);
      setPtoCap(data.pto_cap || 40);
      setShowStudioRemainder(data.show_studio_remainder_to_staff || false);
      setBirthdayMode(data.birthday_announcement_mode || "draft");
    } catch (error) {
      console.error("Failed to load admin settings");
    }
  };

  const saveAdminSettings = async () => {
    setAdminLoading(true);
    try {
      await api.post("/admin/settings", {
        overhead_percent: overhead,
        card_fee_percent: cardFee,
        pto_accrual_rate: ptoAccrual,
        pto_cap: ptoCap,
        show_studio_remainder_to_staff: showStudioRemainder,
        birthday_announcement_mode: birthdayMode
      });
      toast.success("Admin settings saved");
    } catch (error) {
      toast.error("Failed to save admin settings");
    } finally {
      setAdminLoading(false);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      await api.post("/employee/settings", {
        notifications,
        share_birthday: sharebirthday
      });
      toast.success("Settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setPasswordLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Password changed successfully");
      setShowPasswordChange(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F1] p-6 pb-24">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-serif text-[#231E1A] mb-6">Settings</h1>
        
        <div className="bg-white rounded-lg p-6 mb-4">
          <h2 className="text-lg font-semibold mb-4 text-[#231E1A]">Preferences</h2>
          
          <div className="flex items-center justify-between py-3 border-b border-[#EAE1D4]">
            <div>
              <div className="font-medium text-[#231E1A]">Notifications</div>
              <div className="text-sm text-[#9C6B4A]">Receive updates about your earnings and PTO</div>
            </div>
            <input
              type="checkbox"
              checked={notifications}
              onChange={(e) => setNotifications(e.target.checked)}
              className="h-5 w-5 accent-[#9C6B4A]"
            />
          </div>
          
          <div className="flex items-center justify-between py-3">
            <div>
              <div className="font-medium text-[#231E1A]">Share my birthday with the team</div>
              <div className="text-sm text-[#9C6B4A]">Allow team birthday announcements</div>
            </div>
            <input
              type="checkbox"
              checked={sharebirthday}
              onChange={(e) => setShareBirthday(e.target.checked)}
              className="h-5 w-5 accent-[#9C6B4A]"
            />
          </div>
        </div>
        
        <button
          onClick={saveSettings}
          disabled={loading}
          className="w-full bg-[#9C6B4A] text-white py-3 rounded-lg hover:bg-[#8B5A39] transition disabled:opacity-50 mb-4 font-medium"
        >
          {loading ? "Saving..." : "Save Settings"}
        </button>

        <div className="bg-white rounded-lg p-6 mb-4">
          <h2 className="text-lg font-semibold mb-4 text-[#231E1A]">Security</h2>
          
          {!showPasswordChange ? (
            <button
              onClick={() => setShowPasswordChange(true)}
              className="w-full py-3 px-4 border border-[#9C6B4A] text-[#9C6B4A] rounded-lg hover:bg-[#FAF7F1] transition font-medium"
            >
              Change Password
            </button>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#231E1A] mb-2">Current Password</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-[#EAE1D4] rounded-lg focus:outline-none focus:border-[#9C6B4A]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#231E1A] mb-2">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-[#EAE1D4] rounded-lg focus:outline-none focus:border-[#9C6B4A]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#231E1A] mb-2">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-[#EAE1D4] rounded-lg focus:outline-none focus:border-[#9C6B4A]"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handlePasswordChange}
                  disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                  className="flex-1 bg-[#9C6B4A] text-white py-3 rounded-lg hover:bg-[#8B5A39] transition disabled:opacity-50 font-medium"
                >
                  {passwordLoading ? "Changing..." : "Change Password"}
                </button>
                <button
                  onClick={() => {
                    setShowPasswordChange(false);
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                  className="px-6 py-3 border border-[#EAE1D4] text-[#231E1A] rounded-lg hover:bg-[#FAF7F1] transition font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {isAdminView && (
          <div className="bg-white rounded-lg p-6 mb-4 border-2 border-[#B0905E]">
            <h2 className="text-lg font-semibold mb-1 text-[#231E1A]">Administrator Settings</h2>
            <p className="text-sm text-[#9C6B4A] mb-6">Changes recompute downstream figures</p>
            
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#231E1A] mb-2">Overhead %</label>
                  <input
                    type="number"
                    step="0.1"
                    value={overhead}
                    onChange={(e) => setOverhead(parseFloat(e.target.value))}
                    className="w-full px-4 py-2 border border-[#EAE1D4] rounded-lg focus:outline-none focus:border-[#9C6B4A]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#231E1A] mb-2">Card Fee %</label>
                  <input
                    type="number"
                    step="0.1"
                    value={cardFee}
                    onChange={(e) => setCardFee(parseFloat(e.target.value))}
                    className="w-full px-4 py-2 border border-[#EAE1D4] rounded-lg focus:outline-none focus:border-[#9C6B4A]"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#231E1A] mb-2">PTO Accrual Rate (per hour)</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={ptoAccrual}
                    onChange={(e) => setPtoAccrual(parseFloat(e.target.value))}
                    className="w-full px-4 py-2 border border-[#EAE1D4] rounded-lg focus:outline-none focus:border-[#9C6B4A]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#231E1A] mb-2">PTO Cap (hours)</label>
                  <input
                    type="number"
                    step="1"
                    value={ptoCap}
                    onChange={(e) => setPtoCap(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border border-[#EAE1D4] rounded-lg focus:outline-none focus:border-[#9C6B4A]"
                  />
                </div>
              </div>
              
              <div className="border-t border-[#EAE1D4] pt-4">
                <div className="flex items-center justify-between py-3 border-b border-[#EAE1D4]">
                  <div>
                    <div className="font-medium text-[#231E1A]">Show studio remainder to staff</div>
                    <div className="text-sm text-[#9C6B4A]">Display GT profit lines in service transparency</div>
                  </div>
                  <input
                    type="checkbox"
                    checked={showStudioRemainder}
                    onChange={(e) => setShowStudioRemainder(e.target.checked)}
                    className="h-5 w-5 accent-[#9C6B4A]"
                  />
                </div>
                
                <div className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium text-[#231E1A]">Birthday announcement mode</div>
                    <div className="text-sm text-[#9C6B4A]">Draft requires approval, auto posts immediately</div>
                  </div>
                  <select
                    value={birthdayMode}
                    onChange={(e) => setBirthdayMode(e.target.value)}
                    className="px-4 py-2 border border-[#EAE1D4] rounded-lg focus:outline-none focus:border-[#9C6B4A] text-[#231E1A]"
                  >
                    <option value="draft">Draft</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>
              </div>
            </div>
            
            <button
              onClick={saveAdminSettings}
              disabled={adminLoading}
              className="w-full bg-[#B0905E] text-white py-3 rounded-lg hover:bg-[#9C804E] transition disabled:opacity-50 font-medium mt-6"
            >
              {adminLoading ? "Saving..." : "Save Admin Settings"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}